import { Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { STATUS_COLORS } from '../../../../constants';
import { ratioHistoryConfig } from '../../../config/metricHistory';
import { IModalFilterContext, IRatioDetailsData } from '../../../types';
import { formatActiveStatuses } from '../../../utils/formatActiveStatuses';
import { ExplainerBox } from '../../shared/ExplainerBox';
import { FormulaBox } from '../../shared/FormulaBox';
import { FormulaLine } from '../../shared/FormulaLine';
import { MetricDetailsModal } from '../../shared/MetricDetailsModal';
import { MetricHistoryChart } from '../../shared/MetricHistoryChart';
import { MetricsDivider } from '../../shared/MetricsDivider';
import { StatRow } from '../../shared/StatRow';

const { Text } = Typography;

interface IRatioDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  data: IRatioDetailsData | null;
  filter: IModalFilterContext;
  repeatDonationsThreshold?: number;
  historyDataCutoff?: string;
}

const RatioFormulaBox: FC<{ data: IRatioDetailsData }> = ({ data }) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.ratioModal');
  const total = data.firstTimeDonorCount + data.repeatDonorCount;
  const pct = total > 0 ? data.firstTimeDonorCount / total : 0;
  const candidates = [1, 2, 3, 5].map((d) => {
    const exact = d / (pct || 1);
    const rounded = Math.round(exact);
    return { d, exact, rounded, error: Math.abs(exact - rounded) };
  });

  return (
    <FormulaBox title={t('formulaBoxTitle')}>
      <FormulaLine>
        {t('formulaFirstTimePct')} {data.firstTimeDonorCount} / {total} ={' '}
        {(pct * 100).toFixed(1)}%
      </FormulaLine>
      <FormulaLine marginTop={4}>{t('formulaCandidates')}</FormulaLine>
      {candidates.map((c) => (
        <Text
          key={c.d}
          style={{
            color:
              c.d === data.ratioFirstTime
                ? data.statusColor
                : BRAND_COLORS.secondary.lightSage,
            fontSize: 12,
            fontFamily: 'monospace',
            display: 'block',
            fontWeight: c.d === data.ratioFirstTime ? 700 : 400,
          }}
        >
          {'  '}
          {c.d} / {pct.toFixed(4)} = {c.exact.toFixed(4)} → {c.d}:{c.rounded}{' '}
          (err {c.error.toFixed(4)}){c.d === data.ratioFirstTime ? ' ✓' : ''}
        </Text>
      ))}
      <Text
        style={{
          color: BRAND_COLORS.secondary.lightSage,
          fontSize: 11,
          display: 'block',
          marginTop: 4,
          fontStyle: 'italic',
        }}
      >
        {t('formulaNote')}
      </Text>
      <FormulaLine marginTop={4}>
        {t('formulaResultLabel')}{' '}
        <span style={{ color: data.statusColor, fontWeight: 700 }}>
          {data.ratioFirstTime} : {data.ratioRepeat}
        </span>
      </FormulaLine>
    </FormulaBox>
  );
};

export const RatioDetailsModal: FC<IRatioDetailsModalProps> = ({
  visible,
  onClose,
  data,
  filter,
  repeatDonationsThreshold,
  historyDataCutoff,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.ratioModal');
  if (!data) return null;

  const totalDonors = data.firstTimeDonorCount + data.repeatDonorCount;
  const firstTimePct =
    totalDonors > 0
      ? Math.round((data.firstTimeDonorCount / totalDonors) * 100)
      : 0;
  const repeatPct =
    totalDonors > 0
      ? Math.round((data.repeatDonorCount / totalDonors) * 100)
      : 0;

  return (
    <MetricDetailsModal
      visible={visible}
      onClose={onClose}
      data={data}
      title={t('title')}
      tooltip={t('tooltip', {
        statuses: formatActiveStatuses(filter.activeDonorStatuses),
      })}
      sectionLabel={t('sectionCurrentPeriod')}
      headerValue={`${data.ratioFirstTime} : ${data.ratioRepeat}`}
      explainer={
        <ExplainerBox>
          <strong style={{ color: '#fff' }}>{t('explainerTitle')}</strong>{' '}
          {t('explainerDef')}
        </ExplainerBox>
      }
      historySection={
        <MetricHistoryChart
          config={ratioHistoryConfig}
          live={data}
          filter={filter}
          extra={{ repeatDonationsThreshold, historyDataCutoff }}
          translationNs="dashboard.RecruiterDashboard.ratioModal"
        />
      }
    >
      <StatRow label={t('statTotalActive')} value={data.activeDonorCount} />
      <StatRow
        label={t('statFirstTimeDonors')}
        value={`${data.firstTimeDonorCount} (${firstTimePct}%)`}
        color="#1890ff"
        indent
      />
      <StatRow
        label={t('statRepeatDonors')}
        value={`${data.repeatDonorCount} (${repeatPct}%)`}
        color={STATUS_COLORS.good.fill}
        indent
      />
      <MetricsDivider />
      <RatioFormulaBox data={data} />
    </MetricDetailsModal>
  );
};
